<script lang="ts">
  import { type Snippet } from 'svelte';
  import { onMount, onDestroy } from 'svelte';

  import Title from '$shared/atoms/Title/Title.svelte';
  import Loader from '$shared/atoms/Loader/Loader.svelte';
  import ConfirmActions from '$shared/atoms/ConfirmActions/ConfirmActions.svelte';
  import { browser } from '$app/environment';

  interface Props {
    title?: string;
    description?: string;
    okLabel?: string;
    actionRequired?: boolean;
    cancelLabel?: string;
    hideCancel?: boolean;
    loading?: boolean;
    processing?: boolean;
    disabled?: boolean;
    name?: string;
    customActions?: boolean;
    headerAction?: Snippet | undefined;
    footerAction?: Snippet | undefined;
    children?: Snippet | undefined;
    onok?: () => void;
    oncancel?: () => void;
  }

  let {
    title = 'Entity Form',
    description = '',
    okLabel = 'Verify',
    actionRequired = true,
    cancelLabel = '',
    hideCancel = true,
    loading = false,
    processing = false,
    disabled = false,
    name = '',
    customActions = false,
    headerAction = undefined,
    footerAction = undefined,
    children = undefined,
    onok,
    oncancel
  }: Props = $props();

  let formTitle: any = $state();
  let entityFormElem: any = $state();
  let container: any = $state();
  let formHeaderHeight = 0;
  let containerHeight = 0;
  let entityFormElemheight = 0;

  let fixedActions = $state(false);
  let requestAnimationFrame = (fn) => window.setTimeout(fn, 20);
  if (browser) {
    requestAnimationFrame =
      window?.requestAnimationFrame ||
      window?.mozRequestAnimationFrame ||
      window?.webkitRequestAnimationFrame ||
      ((fn) => window.setTimeout(fn, 20));
  }

  class EventQueue {
    q = [];
    add = (ev: any) => {
      this.q.push(ev);
    };

    call() {
      for (let i = 0, j = this.q.length; i < j; i++) {
        (this.q[i] as EventQueue).call();
      }
    }
  }

  onMount(() => {
    calculateHeight();
    if (browser) {
      attachResizeEvent(entityFormElem, onEntityFormResize);
      window.addEventListener('resize', onWindowResize);
    }
  });

  onDestroy(() => {
    if (browser) {
      window.removeEventListener('resize', onWindowResize);
    }
  });

  const attachResizeEvent = (element, resized) => {
    if (!element.resizedAttached) {
      element.resizedAttached = new EventQueue();
      element.resizedAttached.add(resized);
    } else if (element.resizedAttached) {
      element.resizedAttached.add(resized);
      return;
    }

    element.resizeSensor = element.querySelector('.resize-sensor');

    const expand = element.resizeSensor.childNodes[0];
    const shrink = element.resizeSensor.childNodes[1];

    const reset = function () {
      expand.scrollLeft = 100000;
      expand.scrollTop = 100000;

      shrink.scrollLeft = 100000;
      shrink.scrollTop = 100000;
    };

    reset();
    let dirty = false;

    const dirtyChecking = function () {
      if (!element.resizedAttached) return;

      if (dirty) {
        element.resizedAttached.call();
        dirty = false;
      }

      requestAnimationFrame(dirtyChecking);
    };

    requestAnimationFrame(dirtyChecking);
    let lastWidth, lastHeight;
    let cachedWidth, cachedHeight; //useful to not query offsetWidth twice

    const onScroll = function () {
      if (
        (cachedWidth = element.offsetWidth) != lastWidth ||
        (cachedHeight = element.offsetHeight) != lastHeight
      ) {
        dirty = true;

        lastWidth = cachedWidth;
        lastHeight = cachedHeight;
      }
      reset();
    };

    const addEvent = function (el, name, cb) {
      if (el.attachEvent) {
        el.attachEvent('on' + name, cb);
      } else {
        el.addEventListener(name, cb);
      }
    };

    addEvent(expand, 'scroll', onScroll);
    addEvent(shrink, 'scroll', onScroll);
  };

  const onEntityFormResize = () => {
    calculateHeight();
  };

  const onWindowResize = () => {
    calculateHeight();
  };

  const calculateHeight = (): void => {
    formHeaderHeight = formTitle?.getBoundingClientRect()?.height || 0;
    entityFormElemheight = entityFormElem?.getBoundingClientRect()?.height || 0;
    containerHeight = container?.getBoundingClientRect()?.height || 0;
    fixedActions = entityFormElemheight > containerHeight - formHeaderHeight;
  };

  export const scrollToErrorView = () => {
    entityFormElem?.scrollIntoView();
  };
</script>

<div data-cy={`${name}-entityForm`} class="entity-form">
  <div bind:this={container} class="form-container">
    <div class="top-header">
      <div bind:this={formTitle} data-cy="form-title" class="title">
        {#if processing}
          <div class="title-loader"><Loader /></div>
        {:else}
          <Title type="h2">{title}</Title>
        {/if}
        {#if !processing}
          <div class="header-action">
            {@render headerAction?.()}
          </div>
        {/if}
      </div>
      <div class="description">{description}</div>
    </div>

    {#if processing}
      <div class="loader"><Loader count={3} /></div>
    {:else}
      <div bind:this={entityFormElem} class="form">
        {@render children?.()}
        <div class="resize-sensor">
          <div class="resize-sensor-expand">
            <div class="resize-sensor-expand-child">{''}</div>
          </div>
          <div class="resize-sensor-shrink">
            <div class="resize-sensor-shrink-child">{''}</div>
          </div>
        </div>
      </div>
      {#if actionRequired}
        <div class="actions" class:fixed={fixedActions}>
          {#if !customActions}
            <ConfirmActions
              {disabled}
              {loading}
              {hideCancel}
              {okLabel}
              {cancelLabel}
              {onok}
              {oncancel}
            />
          {/if}
          {#if !loading}
            <div class="footer-action">
              {@render footerAction?.()}
            </div>
          {/if}
        </div>
      {/if}
    {/if}
  </div>
</div>

<style>
  .entity-form {
    position: relative;
    height: 100vh;
    padding-bottom: 80px;
  }
  .form-container {
    width: 100%;
    height: 100%;
    overflow: auto;
  }
  .title-loader {
    width: 100px;
  }
  .loader {
    padding: 24px;
  }
  .top-header {
    display: flex;
    flex-direction: column;
    padding: 24px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.07);
  }
  .entity-form .title {
    display: flex;
    justify-content: space-between;
  }
  .form {
    padding: 24px;
    position: relative;
  }
  .description {
    color: var(--blue);
    margin-top: 5px;
  }
  .actions {
    position: absolute;
    bottom: auto;
    left: 0;
    width: 100%;
    background-color: #fff;
    display: flex;
    border-top: 1px solid rgba(0, 0, 0, 0.07);
    justify-content: space-between;
    padding: 24px;
  }
  .actions.fixed {
    bottom: 0;
  }
  .header-action {
    position: relative;
    top: 5px;
  }
  .footer-action {
    position: relative;
  }
  .resize-sensor,
  .resize-sensor-shrink,
  .resize-sensor-expand {
    position: absolute;
    left: 0;
    top: 0;
    right: 0;
    bottom: 0;
    overflow: hidden;
    z-index: -1;
    visibility: hidden;
    opacity: 0;
    direction: ltr;
  }
  .resize-sensor-expand-child {
    position: absolute;
    left: 0;
    top: 0;
    transition: 0s;
    width: 100000px;
    height: 100000px;
  }
  .resize-sensor-shrink-child {
    position: absolute;
    left: 0;
    top: 0;
    transition: 0s;
    width: 200%;
    height: 200%;
  }
</style>
